/**
 * @dev This file is imported by the standalone widget bundles
 * (src/chat-widget and src/chat-widget-v2) that ship on third-party host
 * pages. Keep it dependency-free — NO React, NO utilities, NO logging.
 * Webpack's DefinePlugin inlines `process.env.REACT_APP_*` references,
 * which is fine; anything else will break the widget builds.
 */

export const MEDIA_QUERY_769 = 769;
export const COLOR_VIOLET = '#7D61A7';
export const COLOR_LIGHT_DARK = '#181818';

export const CHAT_TIME_LIMIT_SECONDS = 60;

export const CHAT_MAX_MESSAGES_UNREGISTER = 10;
// HORO-10478: how long an anonymous visitor's message allowance lasts before it
// refills. Matches the backend's FREE_PLAN_PERIOD, which is what a registered
// free account gets — one definition of "a month" across both tiers, so a
// support answer doesn't change depending on whether someone has an account.
// Rolling from when the window was armed, not a calendar month, for the same
// reason: that is how `makeUsersFree` advances plan.validUntil.
export const ANON_MESSAGES_PERIOD_DAYS = 30;

export const DEFAULT_REQUEST_TIMEOUT = 10000;

export const UNAUTHORIZED_RESPONSE_CODE_REFRESH = 401;

export const UNAUTHORIZED_RESPONSE_CODE_REJECT = 403;

export const REQUEST_FAILED = 404;

export const BAD_REQUEST = 400;

export const SERVER_ERROR = 500;

export const ASTROLOGY_BASE_URL = 'https://www.astrology.com';

export const ASTROLOGY_BASE_URL_PRIVACY = `${ASTROLOGY_BASE_URL}/privacy.html`;
export const REVIEW_FORM_LINK = 'https://docs.google.com/forms/d/e/1FAIpQLScSDLwLrF1BmZ45X1DsdBwtZrdg4TuSiVcZSl8qUy8WA-rakw/viewform';
export const SHARE_LANDING_LINK = 'https://veda.astrology.com/?utm_source=sharing&utm_medium=chat';
export const APP_URL_CHAT_HISTORY = 'chat-history';
export const APP_URL_CHAT = 'advisor';
export const APP_URL_REDIRECT_CHAT = 'chat';
export const APP_URL_CHAT_VEDA = 'advisor';
export const APP_URL_CHAT_VEDA_SHARE = 'share-chat';
export const APP_URL_CHAT_SHARE = 'share-chat';
export const APP_URL_NOT_FOUND = 'not-found';
export const APP_URL_USER_DELETION_STATUS = 'user-deletion-status';
export const APP_URL_HOME = '/';
export const APP_URL_PROFILE = 'profile';
// HORO-10533: Profile v2 detail routes — children of `/profile`. The URL, not a
// breakpoint check, is the single source of truth for which detail is open; CSS
// alone decides whether it renders as a bottom sheet over the master (mobile)
// or a pane beside it (desktop). See components/pages/ProfileV2.
//
// Deliberately flat. Both breakpoints show every section expanded in one
// column, so there are no intermediate "Personal Information" /
// "Account Management" list screens to route to — each editor hangs directly
// off /profile.
//
// These two aren't routes; they're the section identifiers, used as the keys
// for the section list the master maps over.
export const PROFILE_SECTION_PERSONAL = 'personal';
export const PROFILE_SECTION_ACCOUNT = 'account';
export const APP_URL_PROFILE_BIRTH_DATE = 'birth-date';
export const APP_URL_PROFILE_BIRTH_TIME = 'birth-time';
export const APP_URL_PROFILE_BIRTH_PLACE = 'birth-place';
export const APP_URL_PROFILE_PHONE = 'phone';
// HORO-10552: reachable only while the account has no email — a Facebook
// signup that declined the permission. Once one is on file the row goes back to
// display-only, since there is no verified way to change it (HORO-10537 was
// dropped and the unverified write on POST /user is not the answer).
export const APP_URL_PROFILE_EMAIL = 'email';
export const APP_URL_PROFILE_USAGE = 'usage';
export const APP_URL_PROFILE_PLAN = 'plan';
export const APP_URL_PROFILE_PAYMENT_METHOD = 'payment-method';
export const APP_URL_PROFILE_PAYMENT_HISTORY = 'payment-history';
export const APP_URL_LANDING = 'landing';
export const APP_URL_CATALOG = 'catalog';
export const APP_URL_WIDGET = 'widget';
export const APP_URL_WIDGET_CHAT = 'widget-chat';
export const APP_URL_SUBSCRIPTION = 'subscription';
export const APP_URL_PAYMENT = 'payment';
// HORO-10478: the redesigned subscription checkout. Deliberately a separate
// route from the legacy `/payment` page, which still serves trial and
// message-bundle purchases until it's retired.
export const APP_URL_CHECKOUT = 'checkout';
// HORO-10478: master switch for the free trial. Off means new customers are
// offered the subscription directly instead.
//
// Turning the trial off also turns the discount offer off, because the offer
// flow is built on top of it: both `tryToCreateOffer` and
// `tryToCreateOfferRedirect` set the modal to the trial variant for anyone who
// hasn't trialled, and OfferPaymentContent's CTA starts a trial for those users.
// Leaving offers on with trials off would put a "Start free trial" button back
// in front of the customer through the side door.
//
// Note the backend keeps its own, independent offer switch — the `OFFER_ENABLED`
// row in the Config collection, which controls whether offer *pricing* applies.
// This flag does not touch it.
export const FREE_TRIAL_ENABLED = false;
// HORO-10479: the designed post-purchase confirmation, replacing the generic
// `/payment-result/success`. It must be a real route because a 3DS challenge
// redirects the whole browser to Checkout and back to this `success_url`.
export const APP_URL_PAYMENT_SUCCESS = 'payment/success';
export const APP_URL_PAYMENT_HISTORY = 'payments-history';
export const APP_URL_PAYMENT_RESULT = 'payment-result';
export const APP_URL_ACTIVITIES = 'activities';
export const APP_URL_ACTIVITY = 'activity';
export const APP_URL_MEMORY = 'memory';
export const APP_URL_LIVE_READINGS = 'live-readings';
// HORO-10703: public Free vs Premium comparison page.
export const APP_URL_PLANS = 'plans';

// Tier keys as returned by GET /subscriptions/tiers. Mirrors the backend's
// `mongo/models/tierDefinition.consts.ts`. `subscriber_legacy` is never in the
// public catalog — it is a status existing holders are in, not a plan on sale.
export const TIER_KEY = {
    ANONYMOUS: 'anonymous',
    REGISTERED: 'registered',
    SUBSCRIBER: 'subscriber'
};
// HORO-10426: the pre-redesign promo landing page is kept reachable at
// /classic (non-crawlable — see robots meta in LandingPage) while the new
// anonymous hero homepage takes over `/`.
export const APP_URL_HOME_CLASSIC = '/classic';

export const BASE_URL = process.env.REACT_APP_API_BASE_URL;
export const BASE_APP_URL = process.env.REACT_APP_BASE_URL;
export const GOOGLE_AUTH_URL = `${BASE_URL}/auth/google`;
export const FACEBOOK_AUTH_URL = `${BASE_URL}/auth/facebook`;
export const CHECKOUT_PUBLIC_KEY = process.env.REACT_APP_CHECKOUT_PUBLIC_KEY;
// HORO-10451 — CrazyEgg account number (8 digits, e.g. '00549225', shared
// with astrology.com). Loading is gated on this being set, so dev/staging
// simply leave it blank and no heatmap script is injected.
export const CRAZY_EGG_ID = process.env.REACT_APP_CRAZY_EGG_ID;
export const USE_CHECKOUT_FLOW = process.env.REACT_APP_USE_CHECKOUT_FLOW === 'true';
export const GOOGLE_PAY_MERCHANT_ID = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID;
export const APPLE_PAY_MERCHANT_ID = process.env.REACT_APP_APPLE_PAY_MERCHANT_ID;
export const APP_ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
export const CANONICAL_BASE = process.env.REACT_APP_CANONICAL_BASE;
export const CANONICAL_BASE_PATH = CANONICAL_BASE ? new URL(CANONICAL_BASE).pathname.replace(/\/$/, '') : '';
export const APP_GIT_BRANCH = process.env.REACT_APP_GIT_BRANCH;
export const APP_GIT_COMMIT = process.env.REACT_APP_GIT_COMMIT;

export const DEFAULT_CALENDAR_START_DATE = '1990/01/10';

export const STORAGE_KEY_ACCESS_TOKEN = 'accessToken';
// HORO-10478: the anonymous message tally, `{ [chatId]: count }`. The key is
// deliberately bland — it sits among the other `veda*` keys rather than
// announcing itself as the thing to edit to refill free messages. Friction,
// not security: site data can still be cleared, and only server-side metering
// fixes that.
export const STORAGE_KEY_CHATS_MESSAGES_COUNT = 'vedaChatState';
// The key this replaced. Unlike the expiry below, this one HAS been in
// production for a long time, so an existing visitor's tally has to be carried
// across on first read — otherwise the rename itself hands every anonymous user
// a fresh allowance on deploy day.
export const STORAGE_KEY_CHATS_MESSAGES_COUNT_LEGACY = 'chatsMessagesCount';
// HORO-10478: when the anonymous message pool next rolls back to zero, stored
// as Unix epoch milliseconds — armed ANON_MESSAGES_PERIOD_DAYS ahead each time
// it rolls. Kept in its own key rather than folded into the tally, whose shape
// ({ [chatId]: count }) is read in a dozen places and by already-installed
// clients — a sibling key needs no migration and no defensive parsing.
//
// The key and value are both deliberately unremarkable. An ISO date under a
// key ending "ResetAt" told anyone opening devtools exactly which value to
// edit to refill their free messages. This is friction, not security — site
// data can still be cleared, and only server-side metering fixes that — but
// there is no reason to label the lock.
export const STORAGE_KEY_ANON_MESSAGES_EXP = 'anonMessagesExp';
// No legacy counterpart: this key only ever existed on develop-in-progress, so
// there is no production visitor carrying the name it replaced.
export const STORAGE_KEY_SAVED_USER_MESSAGES = 'chatsSavedMessages';
export const STORAGE_KEY_USER_TERM_ACCEPTED = 'userTermAccepted';
export const STORAGE_KEY_ACTIVE_CHAT = 'activeChatId';
export const UNREG_CHATS_ID = 'vedaUnregisterChatsId';
export const STORAGE_KEY_SELF_CREATED_CHAT = 'selfCreated';
export const STORAGE_KEY_USER_START_LOGIN = 'vedaUserStartLogin';
export const STORAGE_KEY_RESEND_MESSAGES = 'resendMessages';
export const STORAGE_KEY_FORCE_LOGOUT = 'wasForcedLogout';
export const STORAGE_KEY_URL_BEFORE_PAYMENT = 'urlBeforePayment';
// HORO-10504: which advisor an anonymous user was blocked from chatting
// with, so App.js's post-login redirect (authModalContext === 'advisorLocked')
// can resume them into that specific advisor's chat after they register —
// same "stash intent, resume after auth" pattern as the tarot upsell's own
// sessionStorage flag, kept as its own key rather than overloading
// `vedaPostLoginContext` (which only ever holds a bare context string).
export const STORAGE_KEY_PENDING_ADVISOR_ALIAS = 'vedaPendingAdvisorAlias';
export const STORAGE_KEY_DATA_FOR_RETRY_PAYMENT = 'dataForRetryPayment';
export const STORAGE_KEY_NO_TYPEWRITER_ANIMATION = 'noTypeWriterAnimation';
export const STORAGE_KEY_USER_VISITED_PAYMENTS = 'isUserVisitedPayments';
// HORO-10479: what the customer just bought, written at submit time. A 3DS
// challenge takes the browser away and brings it back as a fresh page load, so
// the confirmation screen has no in-memory state to read — this is how it knows
// whether to describe a subscription or a message bundle.
export const STORAGE_KEY_LAST_PURCHASE = 'vedaLastPurchase';
// HORO-10679: the message a free-plan customer was blocked from sending, kept
// alive across the paywall → checkout → confirmation round trip so it can send
// itself once the pack they bought is credited. Its own key rather than part of
// STORAGE_KEY_SAVED_USER_MESSAGES: ChatBar deliberately wipes that draft map on
// unmount, which is exactly what navigating to checkout does.
export const STORAGE_KEY_HELD_MESSAGE = 'vedaHeldMessage';
export const STORAGE_KEY_WIDGET_HOST = 'vedaWidgetHost';
export const STORAGE_KEY_WIDGET_SOURCE = 'vedaWidgetSource';
// HORO-10426: once-per-browser-session flag for the AnonHomePage returning-user
// redirect. Shared with App.js's post-login redirect so an explicit login (which
// redirects via App.js) consumes the same budget — otherwise the homepage
// redirect would still fire on the first logo click and bounce the user back
// into a chat instead of landing on the homepage.
export const STORAGE_KEY_LANDING_REDIRECT_DONE = 'vedaLandingRedirectDone';
// HORO-10504: marketing's Zara-launch email links carry this as a standard
// utm_campaign value (e.g. `?utm_source=email&utm_medium=email&utm_campaign=
// zara_launch`) — matched on the campaign specifically (useZaraEmailEntry.js)
// so only this campaign's links open the Zara modal on arrival, not every
// marketing email. Keyed off the existing utm_campaign param rather than a
// bespoke one, consistent with the utm_source/utm_medium parsing
// analytics.js already does for entry-source attribution.
export const QUERY_PARAM_UTM_CAMPAIGN = 'utm_campaign';
export const ZARA_LAUNCH_CAMPAIGN_VALUE = 'zara_launch';

// Max number of unregistered chat IDs kept in localStorage.
// Must match the backend's updateUser Joi validation (chats array .max(20)).
export const UNREG_CHATS_MAX = 20;

// Query param the chat owner's Invite link carries. ChatMembers only
// auto-joins the viewer as a participant when this param is present —
// bare visits to /advisor/:alias/:chatId (e.g. widget redirects,
// bookmarks) no longer silently add the user to someone else's chat.
export const CHAT_INVITE_PARAM = 'invite';

export const STORE_AUTH_PAYMENT_POPUP = {
    trial: 'trial',
    product: 'product',
    plan: 'plan',
    issue: 'issue'
};

export const STORE_PAYMENT_MODAL_TYPE = {
    trial: 'trial',
    subscription: 'supscription'
};

// HORO-10360: what prompted a sign-up, sent to the backend as `registrationSource`
// on email sign-up and (as `_source`) on the OAuth redirect. These are the
// auth-modal contexts the backend knows; anything else is sent as 'unknown'.
// Keep in sync with REGISTRATION_SOURCES in media-ai-veda-backend.
export const REGISTRATION_SOURCES = [
    'birthChart',
    'tarotLimit',
    'advisorLocked',
    'advisorSubscribe',
    'chatHistory',
    'memory',
    'login',
    'emailLogin',
    'messageLimit',
    // HORO-10703: registered from the /plans page's Premium CTA.
    'plansSubscribe'
];

export const toRegistrationSource = (context) => (REGISTRATION_SOURCES.includes(context) ? context : 'unknown');

// HORO-10635: which upsell surface converted a new subscriber, sent to the
// backend as `subscriptionSource` on the checkout session request. Each value
// names the window the user subscribed from, not what led them to it; anything
// unrecognised is sent as 'unknown'. Last touch wins in the browser; the
// backend stamps the user's first subscription only.
// Keep in sync with SUBSCRIPTION_SOURCES in media-ai-veda-backend.
export const SUBSCRIPTION_SOURCES = [
    'messageLimit',
    'advisorLocked',
    'zaraUpsell',
    'loveTarot',
    'tarotLimit',
    'profileAccount',
    'subscriptionBanner',
    'paymentRetry',
    'winbackOffer',
    'checkoutDirect',
    // HORO-10703: the standalone /plans comparison page.
    'plansPage'
];

export const toSubscriptionSource = (source) => (SUBSCRIPTION_SOURCES.includes(source) ? source : 'unknown');

// HORO-10679: the one source that raises the in-chat message-pack offer when
// the paywall is closed. Named rather than inlined because two places have to
// agree on it — the paywall that reads it and the offer that sends it back
// into checkout so the purchase keeps its attribution.
export const SUBSCRIPTION_SOURCE_MESSAGE_LIMIT = 'messageLimit';

// A carried source only counts on the calendar day (in the customer's own
// timezone) the upsell was seen. Past that the customer has made up their own
// mind, so the purchase is `checkoutDirect` rather than credited to a surface
// they saw on an earlier day.
const isSameCalendarDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();

// What to send for a checkout, given the payment data it was reached with
// (`source` + `sourceAt`, stamped by useGoToCheckout). No source, or a stale
// one, is a direct checkout.
export const resolveSubscriptionSource = (paymentData, now = Date.now()) => {
    const { source, sourceAt } = paymentData || {};
    if (!source || typeof sourceAt !== 'number' || !isSameCalendarDay(sourceAt, now)) return 'checkoutDirect';
    return source;
};

// HORO-10495/10499/10502: which copy/art the register/sign-up modal
// (EntryStep.js) shows, selected from `authModalContext` (toggleAuthModal's
// `context` payload). `general` is the default, already-shipped copy used
// by every trigger that doesn't map to a more specific variant below —
// adding a genuinely distinct chat-history/memory variant later is a
// one-line addition to REGISTER_UPSELL_VARIANT_BY_CONTEXT below, plus a new
// entry in VARIANT_COPY (EntryStep.js), not a rebuild.
export const REGISTER_UPSELL_VARIANT = {
    GENERAL: 'general',
    TAROT: 'tarot',
    MEMORY: 'memory',
    CHAT_HISTORY: 'chatHistory',
    ADVISOR_LOCKED: 'advisorLocked'
};

// Maps `authModalContext` → REGISTER_UPSELL_VARIANT. Any context not listed
// here (including `null`) falls back to GENERAL — this is intentionally a
// lookup, not a switch, so EntryStep.js/AuthModal.js need zero changes the
// next time a context gains its own distinct variant.
export const REGISTER_UPSELL_VARIANT_BY_CONTEXT = {
    tarotLimit: REGISTER_UPSELL_VARIANT.TAROT,
    memory: REGISTER_UPSELL_VARIANT.MEMORY,
    chatHistory: REGISTER_UPSELL_VARIANT.CHAT_HISTORY,
    // HORO-10504: locked-advisor upsell — its own title/subtitle (no
    // distinct Figma frame, so it still reuses GENERAL's benefit list in
    // EntryStep.js's VARIANT_COPY). HORO-10599: also Zara's own REGISTER
    // gate now (ZaraUpsellModal's onRegister) — she no longer has a
    // separate "pick a plan, then register" resume; an anon viewer
    // registers first, then uses her 3-message trial like any other
    // registered-accessible advisor.
    advisorLocked: REGISTER_UPSELL_VARIANT.ADVISOR_LOCKED,
    // HORO-10563: "pick a plan, then register" upsell for any non-Zara
    // advisor outside both the anon- and registered-accessible rosters
    // (PaymentModal's own anonymous branch, onAnonUpsellTap) — its own
    // context string since it resumes into /checkout (or a specific
    // advisor's chat only if the account they sign into turns out already
    // premium), not the plain "resume into that advisor's chat" App.js's
    // post-login redirect whitelist assumes for 'advisorLocked'.
    advisorSubscribe: REGISTER_UPSELL_VARIANT.ADVISOR_LOCKED
};

export const DEFAULT_CHAT_NAME = 'New chat';

export const CATEGORY_QUERY_NAME = 'category';
export const USER_CHAT_ROLE = {
    USER: 'user',
    SYSTEM: 'system',
    ASSISTANT: 'assistant',
    USER_CHAT_CONNECTED: 'user_chat_connected',
    USER_CHAT_DISCONNECTED: 'user_chat_disconnected'
};

export const PURCHASES_BANNER_TYPE = {
    0: 'Get More Guidance',
    1: 'Empathy Meets AI'
};

export const OG_URL = 'https://veda.astrology.com/?utm_source=sharing&utm_medium=chat';

export const STATIC_PAGES_LINKS = {
    ABOUT: 'https://www.astrology.com/us/aboutus.aspx',
    CONTACT: 'https://www.astrology.com/us/feedback.aspx',
    ADVERTISING: 'https://www.astrology.com/us/advertising-inquiry.aspx',
    PRIVACY: 'https://www.astrology.com/privacy.html',
    TERMS: 'https://www.astrology.com/tos.html'
};

export const SOCIAL_PAGES_LINKS = {
    FACEBOOK: 'https://www.facebook.com/Astrologycom',
    INSTAGRAM: 'https://www.instagram.com/astrologydotcom/',
    YOUTUBE: 'https://www.youtube.com/channel/UCVIPl_wcAVfel6aW99ZELKg/',
    TWITTER: 'https://twitter.com/astrology_com'
};

export const WSS_AUTO_RECONNECT_INTERVAL = 5000;
export const APP_WS_URL = process.env.REACT_APP_WS_URL;
export const CHAT_TYPE = {
    SINGLE: 'single',
    MULTIPLY: 'multiply'
};

// Default advisor alias for users who haven't set a preference. Matches the
// historical implicit default used by the widget and pre-handoff app.
export const DEFAULT_ADVISOR_ALIAS = 'veda';

// HORO-10326: gate the Memory menu item in the sidebar / drawer. The item
// is part of the menu design but not yet shipped — render it but hide
// until the memory feature lands.
export const MEMORY_ENABLED = process.env.REACT_APP_MEMORY_ENABLED === 'true';
export const MEMORY_NEW_BADGE_UNTIL = new Date('2026-07-10');
export const STORAGE_KEY_MEMORY_ANNOUNCEMENT_SEEN = 'vedaMemoryAnnouncementSeen';
export const ANON_MODAL_ENABLED = false;

// HORO-10504: a second, independent switch on top of the backend advisor
// catalog — gates the whole Zara feature (catalog CTA behavior, upsell modal,
// welcome modal, marketing-email entry, and her pin at the top of the
// sidebar/drawer advisor list) so frontend eng has its own lever to pull
// without needing backend/content-team coordination if something breaks
// specifically in the frontend experience. Not a substitute for the backend
// publishing her in the catalog — both need to be true for her to actually be
// usable. Same flip-at-deploy pattern as MEMORY_ENABLED/LIVE_READINGS_ENABLED.
//
// HORO-10581: this is now the ONLY switch on the pin. It used to be ANDed
// with a launch-window date (ZARA_FEATURED_PIN_UNTIL, 2026-09-19) whose
// expiry silently dropped her out of that list on every environment.
export const ZARA_ENABLED = process.env.REACT_APP_ZARA_ENABLED === 'true';

// HORO-10471: Live Readings waitlist — a fake-door demand test for real human
// astrologers. Nothing behind it is live; joining the waitlist IS the signal.
export const LIVE_READINGS_ENABLED = process.env.REACT_APP_LIVE_READINGS_ENABLED === 'true';
// Switch for the promo modal alone — the unprompted interruption that opens over
// a conversation. Off means the waitlist is still reachable from the menu and at
// /live-readings; only the pop-up stops. Kept separate from
// LIVE_READINGS_ENABLED above, which also gates that route and that menu entry,
// so turning the pop-up off doesn't take the whole feature with it.
//
// Note the modal was only ever shown to signed-in users (dismissal is recorded
// on the user document, so there is nowhere durable to record it for an
// anonymous visitor). Off here therefore means off for everyone, and the
export const LIVE_READINGS_PROMO_MODAL_ENABLED = true;
// HORO-10471: how the promo modal was dismissed. Sent to /waitlist/promo-dismiss
// and stored on the user document. GA4 separates these too, but GA4 is sampled
// (ad blockers, declined consent) — the user doc is the complete record, and an
// explicit decline is the only unambiguous "no" this fake-door test collects.
// Must match PROMO_DISMISS_VIA in the backend's waitlist.consts.ts.
export const LIVE_READINGS_DISMISS_VIA = {
    CLOSE: 'close',
    NOT_INTERESTED: 'not_interested'
};

// NOTE: there is deliberately no client-side "already joined" flag for
// anonymous users. Deduplication happens server-side on the email address
// (partial unique index on tenant + email + source), so a device flag added no
// integrity — it only risked hiding the form from a second person on a shared
// device and losing their signal.
export const STORAGE_KEY_ANONYMOUS_MODAL_SEEN = 'vedaAnonModalSeen';

// HORO-10639: Keen (live human psychics) upsell — two anonymous-only
// placements, a dismissible card in the chat thread and a card under the
// catalog grid. Distinct from the Live Readings waitlist above, which asks the
// same demand question of SIGNED-IN users and answers it with a fake door;
// this one sends real traffic to a real partner. Keep the audiences disjoint
// so neither test reads the other's signal.
//
// The destination (with its tracking parameters) comes from marketing and is
// still outstanding, so it is an env var rather than a literal: QA can point
// dev at a test link without a code change, and an unset value renders no card
// at all rather than a link to nowhere. The existing top-nav widget link
// (widgetData.js) is NOT reused on purpose — sharing its `tid` would blend this
// placement into the widget's numbers on Keen's side, which is the one thing
// the test needs to keep apart.
export const KEEN_UPSELL_URL = process.env.REACT_APP_KEEN_UPSELL_URL || '';

// Which placement an event came from, so one funnel can be read per surface.
export const KEEN_UPSELL_SURFACE = {
    CHAT: 'chat',
    CATALOG: 'catalog'
};

// The chat card follows Veda's first reply (HORO-10697: was three, which
// reached too few anonymous visitors). The cold-start intro is a client-side
// render, not a persisted message, and so does not count toward this.
export const KEEN_CHAT_AD_MIN_ADVISOR_REPLIES = 1;

// Dismissal is per conversation — closing the card means "not in this chat",
// so a new conversation offers it again once that thread has earned it. Holds a
// list of chat ids. localStorage (not session) so it survives a reload.
export const STORAGE_KEY_KEEN_CHAT_AD_DISMISSED = 'vedaKeenChatAdDismissed';

// Newest-first cap on that list: a visitor who keeps starting chats shouldn't
// grow one storage entry without bound.
export const KEEN_CHAT_AD_DISMISSED_LIMIT = 50;

// Two framings of the same offer, tested against each other. These ids are what
// GA4 reports on, so they stay put even if the wording is rewritten again.
export const KEEN_CHAT_AD_COPY_VARIANTS = ['live_reading', 'human_take'];

// Assignment is sticky per device: a visitor shown both framings would make the
// impression/click rates uncomparable.
export const STORAGE_KEY_KEEN_CHAT_AD_VARIANT = 'vedaKeenChatAdVariant';

// What counts as a viewable impression, following the IAB display standard —
// half the pixels for one continuous second. The cards are labelled
// advertising, so the advertising convention is the defensible one, and it
// keeps `chat` and `catalog` measured the same way: without it the catalog
// card logged an impression from ~4,000px below the fold.
export const KEEN_VIEWABLE_RATIO = 0.5;
export const KEEN_VIEWABLE_MS = 1000;

// HORO-10326: ordered default advisor list rendered in the sidebar /
// drawer when the user has no recent advisors yet (new / anonymous first
// visit), and used as the padding source when recent < 6. Aliases must
// match advisor docs in the catalog so avatars / expertise text resolve
// against the live catalogue rather than these fallback strings.
// HORO-10383: `expertise` is sourced from the advisor doc once the
// catalog loads; the strings below are first-paint fallbacks only.
export const DEFAULT_ADVISOR_LIST = [
    { alias: 'veda', name: 'Veda', expertise: 'AI Spiritual Guide' },
    { alias: 'ophelia', name: 'Ophelia', expertise: 'AI Tarot Reader' },
    { alias: 'julie', name: 'Julie', expertise: 'AI Dream Interpreter' },
    { alias: 'marie', name: 'Marie', expertise: 'AI Scientific Psychic' },
    { alias: 'harmony', name: 'Harmony', expertise: 'AI Spiritual Clairvoyant' },
    { alias: 'avalon', name: 'Avalon', expertise: 'AI Financial Guru' }
];

// HORO-10426: fixed top-5 advisor order for the anonymous homepage
// (desktop is served by the recents-aware sidebar; this list drives the
// NEW mobile "Meet our top AI spiritual guides" carousel). Aliases are
// hydrated with live name/subtitle/avatar from the catalogue; the labels
// below are first-paint fallbacks matching the Figma copy. `estelle` is
// the corrected name for the design's mislabeled 5th avatar.
export const HOME_TOP_ADVISORS = [
    { alias: 'veda', name: 'Veda', expertise: 'Spiritual Guide' },
    { alias: 'ophelia', name: 'Ophelia', expertise: 'Tarot Reader' },
    { alias: 'atlas', name: 'Atlas', expertise: 'Intuitive Guide' },
    { alias: 'avalon', name: 'Avalon', expertise: 'Financial Guru' },
    { alias: 'estelle', name: 'Estelle', expertise: 'Clairvoyant' }
];

// HORO-10426: pool of preset prompts for the homepage chat input. Three
// are picked at random on each page load (see AnonHomePage). Keep
// entries short so each stays on a single line (incl. mobile). The first
// three are the original Figma copy.
export const HOME_SUGGESTED_QUESTION_POOL = [
    'Am I on right path?',
    'Will love find me?',
    'When will they reach back out?',
    'What does my future hold?',
    'Will my ex come back?',
    'When will I meet my soulmate?',
    'Should I change careers?',
    'What is my life purpose?',
    'How do I let go of the past?',
    'What is blocking my growth?',
    'Am I making the right decision?',
    'What should I expect this month?'
];

// How many prompts to surface under the input at once.
export const HOME_SUGGESTED_QUESTION_COUNT = 3;

// HORO-10459: per-advisor cold-start response chips (ChatPage). Shown after
// the advisor's self-introduction finishes dripping in; tapping one submits it
// verbatim as the user's first message.
//
// Copy is authored per advisor personality and is VERBATIM from the ticket —
// do not paraphrase, reorder, or "fix" the phrasing without a copy sign-off.
// Mobile and desktop share the same list.
//
// Shape: the last chip of every set is the tarot entry point, flagged with
// `tarot: true`. Selecting it starts the yes_no tarot flow (advisor asks what
// the user wants clarity on → card pick → reading) instead of sending a plain
// message. Chip text is still what appears as the user's first bubble.
//
// Counts vary by design: Estelle and Aviva ship 4 chips, everyone else 3. The
// renderer maps over whatever is here, so no per-advisor casing is needed.
export const ADVISOR_COLD_START_CHIPS = {
    veda: [
        { text: 'I feel a bit lost when it comes to love' },
        { text: 'I keep wondering how they really feel' },
        { text: "Pull a card on a decision I'm stuck on", tarot: true }
    ],
    ophelia: [
        { text: "There's someone I keep thinking about" },
        { text: 'Work is weighing on me' },
        { text: "Pull a card on a choice I'm facing", tarot: true }
    ],
    marie: [
        { text: "I can't tell if this person's thinking about me" },
        { text: 'I am confused about my relationship' },
        { text: 'Pull a card on a decision I keep avoiding', tarot: true }
    ],
    estelle: [
        { text: "I feel like I've drifted off my path" },
        { text: "I keep wondering if they're the one" },
        { text: 'Someone close to me feels distant' },
        { text: 'Pull a card on where my path is going', tarot: true }
    ],
    julie: [
        { text: "I had a dream I can't shake" },
        { text: 'I keep having the same dream' },
        { text: "Pull a card on a dream that's stayed with me", tarot: true }
    ],
    zoe: [
        { text: "I don't know whether to text them or wait" },
        { text: "I'm starting to wonder if I'll find love" },
        { text: 'Pull a card on someone I like', tarot: true }
    ],
    zara: [
        { text: 'When will love finally arrive?' },
        { text: 'Where is this relationship going?' },
        { text: 'Should I hold on or let go?' },
        // HORO-10647: premium-only (filtered in ChatPage's coldStartChips).
        { text: 'Pull a yes/no love card', tarot: true, variant: 'love' }
    ],
    harmony: [
        { text: 'Someone in my life leaves me drained' },
        { text: "Part of me doesn't fully trust someone" },
        { text: 'Pull a card on the energy around me', tarot: true }
    ],
    atlas: [
        { text: 'I keep ending up in the same situation' },
        { text: "I can't tell what my gut's telling me" },
        { text: 'Pull a card on a pattern I want to break', tarot: true }
    ],
    avalon: [
        { text: 'I feel stuck in my career' },
        { text: "Money's been weighing on me" },
        { text: 'Pull a card on a money or career decision', tarot: true }
    ],
    providence: [
        { text: 'Someone I care about has gone quiet' },
        { text: "There's someone I can't stop thinking about" },
        { text: "Pull a card on what's ahead", tarot: true }
    ],
    aviva: [
        { text: 'I want to feel wanted again' },
        { text: 'My partner and I feel out of sync' },
        { text: "There's something I've been scared to say out loud" },
        { text: 'Pull a card on someone I want to get closer to', tarot: true }
    ]
};

// HORO-10459: fallback chip set for advisors with no authored copy — new
// advisors ship regularly and a cold start with no conversation starters is
// strictly worse than a generic one. Intentionally personality-neutral.
export const ADVISOR_COLD_START_CHIPS_FALLBACK = [
    { text: "There's something on my mind I need help with" },
    { text: 'I keep wondering how they really feel' },
    { text: "Pull a card on a decision I'm stuck on", tarot: true }
];

// HORO-10326: localStorage key tracking the anonymous user's recent
// advisor aliases (most-recent first, deduped, capped at 6). Mirrors the
// backend `user.recentAdvisorAliases` field for the logged-in case.
export const STORAGE_KEY_RECENT_ADVISORS = 'vedaRecentAdvisorAliases';

// HORO-10327, revised HORO-10562: tarot credits for authed free/trial users
// (cap 5/month, shared between Yes/No and 3-Card Spread). "Month" is the
// user's own plan cycle (`plan.validUntil`), not a fixed 30-day clock — see
// the backend's matching MONTHLY_TAROT_LIMIT (service/tarotCredits/
// index.ts) for the full reasoning. localStorage shape:
//   { used: number, resetAt: ISO-8601 string }
// `resetAt` mirrors whatever the backend's `user.dailyTarot` response says
// (their real plan-cycle end); the localStorage value is carried forward
// (max-merged with backend state) on register/login.
export const STORAGE_KEY_DAILY_TAROT = 'vedaDailyTarot';
export const MONTHLY_TAROT_LIMIT = 5;

// HORO-10504: anonymous users' tarot entitlement is 0, not MONTHLY_TAROT_LIMIT
// — sign up to unlock the first reading. Kept as its own constant (not a
// reuse/override of MONTHLY_TAROT_LIMIT, which stays the authed free/trial
// cap) so the two populations' caps can never be accidentally coupled.
// See useTarotCredits (common/chat/tarot/tarotCredits.js) and the
// backend's matching ANON_TAROT_LIMIT (service/tarotCredits/index.ts).
export const ANON_TAROT_LIMIT = 0;

// HORO-10330: anonymous user birth date — sessionStorage only so it
// clears on tab close (legal posture: narrower retention than the
// daily-tarot pool). Migrated to the user profile on register / sign-
// in via `useAuth.js` if the profile doesn't already carry one.
export const STORAGE_KEY_BIRTH_DATE = 'vedaBirthDate';

// HORO-10360: birth chart flow.
// Anonymous "collect, then gate": the details entered before the sign-up wall,
// in sessionStorage so they clear on tab close (same posture as the birth date).
export const STORAGE_KEY_PENDING_BIRTH_CHART = 'vedaPendingBirthChart';
// Entry-1 nudge suppression — suffixed with the user id. "Maybe Later" stamps it.
export const STORAGE_KEY_BIRTH_CHART_NUDGE_PREFIX = 'bc_nudge_dismissed_';
export const BIRTH_CHART_NUDGE_SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;
// Dev-only override to force the UI on (?birth_chart=1 or localStorage.birth_chart='1').
export const STORAGE_KEY_BIRTH_CHART_OVERRIDE = 'birth_chart';
export const QUERY_PARAM_BIRTH_CHART_OVERRIDE = 'birth_chart';
export const APP_URL_PROFILE_BIRTH_CHART = 'birth-chart';
export const EXTERNAL_CHECKOUT_PROD_JS_LIBRARY_PATH = 'https://risk.checkout.com/cdn/risk/1/risk.js';
export const EXTERNAL_CHECKOUT_DEV_LIBRARY_PATH = 'https://risk.sandbox.checkout.com/cdn/risk/1/risk.js';
export const SEND_MESSAGE_ID = 'send_message_id';

// HORO-10384: discriminator stamped on `sendNewChatMessage.rejected.payload.reason`
// so reducers / callers can branch the retry UX on the actual failure type
// (HTTP 408 from the backend's per-request timeout vs. network vs. other
// 4xx/5xx). Server-side enforces the timeout — the FE just renders the
// inline retry bubble when a 408 lands.
export const SEND_REJECTION_REASON = Object.freeze({
    TIMEOUT: 'timeout',
    NETWORK: 'network',
    SERVER: 'server',
    UNKNOWN: 'unknown'
});

export const ACCEPTED_FILE_LOADER = {
    IMAGE: ['image/png', 'image/jpeg', 'image/jpg']
};

export const DEFAULT_IMAGE_SIZE_LIMIT = 5 * 1024 * 1024;

/**
 * Shared GA4 event names for both the v1 and v2 widget bundles and the
 * React app. Names use v1's historical values to preserve continuity of
 * GA4 data — v1 vs v2 attribution is via the `widget_version` parameter
 * on each event payload, not a different event name.
 */
export const WIDGET_EVENTS = Object.freeze({
    VIEWED: 'veda_widget_viewed',
    OPENED: 'veda_widget_opened',
    PROMPT_SELECTED: 'veda_prompt_selected',
    MESSAGE_SENT: 'veda_message_sent',
    MESSAGE_RECEIVED: 'veda_message_received',
    EXPAND_CLICKED: 'veda_widget_expand_clicked',
    THRESHOLD_REACHED: 'veda_widget_threshold_reached',
    CONTINUE_CLICKED: 'veda_widget_continue_clicked',
    CONTINUE_LINK_CLICKED: 'veda_widget_continue_link_clicked',
    SIGNIN_PROMPTED: 'veda_widget_signin_prompted',
    // Re-opening a collapsed chat inside the widget. Distinct from the app's
    // `veda_widget_chat_resumed`, which only fires on the Veda chat page when a
    // widget user lands there (HORO-10578 — the two shared a name and double-counted).
    CHAT_REOPENED: 'veda_widget_chat_reopened',
    TERMS_ACCEPTED: 'veda_widget_terms_accepted',
    TERMS_DECLINED: 'veda_widget_terms_declined'
});

/**
 * HORO-10356 — GA4 event names for the Birth Chart widget.
 * Each event carries a `bc_device_id` (persisted UUID) and a
 * `bc_session_id` (minted on form submit). Events marked "1×"
 * are deduplicated per session via an in-memory Set.
 */
export const BIRTH_CHART_EVENTS = Object.freeze({
    IMPRESSION: 'widget_birth_chart_impression',
    INTENT: 'widget_birth_chart_intent',
    LOADING_SUCCESS: 'widget_birth_chart_loading_success',
    SUN_VIEWED: 'widget_birth_chart_sun_viewed',
    MOON_VIEWED: 'widget_birth_chart_moon_viewed',
    RISING_VIEWED: 'widget_birth_chart_rising_viewed',
    RESULTS_VIEWED: 'widget_birth_chart_results_viewed',
    RESULTS_SENT: 'widget_birth_chart_results_sent'
});

/** localStorage key for the birth-chart widget device identifier. */
export const STORAGE_KEY_BC_DEVICE_ID = 'veda_bc_device_id';

/**
 * HORO-10509 — GA4 funnel events for the email+password auth flow (login,
 * registration, and the register-flow's set-password/verify-code steps).
 * Distinct from the pre-existing `gaEvents.login`/`signUp` macro events —
 * these track per-step attempts/failures for drop-off analysis.
 */
export const AUTH_EVENTS = Object.freeze({
    LOGIN_BUTTON_CLICK: 'auth_login_button_click',
    REGISTER_CLICK: 'auth_register_click',
    // Modal opened by the app (protected-route landing), not by a user click.
    MODAL_AUTO_OPEN: 'auth_modal_auto_open',
    LOGIN_ATTEMPT: 'auth_login_attempt',
    LOGIN_SUCCESS: 'auth_login_success',
    LOGIN_FAILURE: 'auth_login_failure',
    REGISTER_EMAIL_SUBMIT: 'auth_register_email_submit',
    REGISTER_EMAIL_FAILURE: 'auth_register_email_failure',
    SET_PASSWORD_ATTEMPT: 'auth_set_password_attempt',
    SET_PASSWORD_FAILURE: 'auth_set_password_failure',
    CODE_EMAIL_SENT: 'auth_code_email_sent',
    VERIFY_CODE_ATTEMPT: 'auth_verify_code_attempt',
    VERIFY_CODE_FAILURE: 'auth_verify_code_failure',
    ACCOUNT_CREATED: 'auth_account_created'
});
